"use client";

import React, { ReactNode, useEffect, useState } from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import LogoWithContextMenu from "../LogoWithContextMenu";
import AuthButton from "../AuthButton";
import SignupButton from "../auth/SignupButton";
import HeaderNavigation from "./HeaderNavigation";
import SiteFooter from "./SiteFooter";
import { useIsMobile } from "@/hooks/use-mobile";
import { useCrossDomainSession } from "@/hooks/useCrossDomainSession";
import { X, Menu, Download, ArrowUpRight } from "lucide-react";
import {
  designerLinks,
  eventsLink,
  hiringLinks,
  isCurrentPage,
} from "./navLinks";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useAuthButton } from "@/hooks/useAuthButton";
import { INTRO_CALL_URL } from "@/constants/homepage";
import { PORTFOLIO_REVIEW_FREE_CAL_URL } from "@/components/portfolio-review/config";

const SearchBar = dynamic(() => import("@/components/SearchBar"));
const NotificationBell = dynamic(
  () => import("@/components/domains/notifications/NotificationBell"),
);
const ReportDownloadModal = dynamic(
  () => import("@/components/shared/ReportDownloadModal"),
);

interface HomePageLayoutProps {
  children: ReactNode;
}

const HEADER_LAYOUT_STYLE = {
  "--announcement-height": "36px",
  "--header-height": "72px",
} as React.CSSProperties;

const HomePageLayout: React.FC<HomePageLayoutProps> = ({ children }) => {
  const isMobile = useIsMobile();
  const pathname = usePathname();
  const { user, isAuthenticated, isLoading } = useCrossDomainSession({
    enableDebug: false,
  });
  const { handleLogout, isLoggingOut } = useAuthButton();
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  // On mobile landing pages, the hero has its own primary CTA, so we hold the
  // header CTA back until the hero has scrolled past (i.e. the second section
  // is in view) to avoid two competing buttons on screen at once.
  const [pastHero, setPastHero] = useState(false);

  // Detect if we're on a custom domain (hide nav/footer)
  const [isCustomDomain, setIsCustomDomain] = useState(false);
  useEffect(() => {
    const hostname = window.location.hostname;
    const isCustom =
      !hostname.includes("world-class.design") &&
      !hostname.endsWith(".netlify.app") &&
      !hostname.endsWith(".localhost") &&
      hostname !== "localhost" &&
      hostname !== "127.0.0.1";
    setIsCustomDomain(isCustom);
  }, []);

  // Check if we're on a profile page
  const isProfilePage =
    pathname.startsWith("/profile") || pathname.startsWith("/u/");
  const isApplicationPage =
    pathname === "/apply" ||
    pathname.startsWith("/apply/") ||
    pathname === "/designers" ||
    pathname === "/join";

  const isDashboardPage = pathname.includes("/dashboard");
  const isSettingsOrNotificationsPage =
    pathname.includes("/settings") || pathname.includes("/notifications");

  const showFooter = !isDashboardPage && !isSettingsOrNotificationsPage;

  // Announcement banner shows on every public page (not the app/dashboard),
  // and hides on scroll. Landing pages absorb it in their hero padding; other
  // pages keep a stable banner + header spacer to avoid a jump on scroll.
  const isLandingPage = pathname === "/" || pathname === "/portfolio-review";
  const showAnnouncement = !isCustomDomain && showFooter && !scrolled;

  // Contextual header CTA: the portfolio-review page books the free 15-min
  // review; every other public page books the recruiter intro call.
  const isReviewPage = pathname === "/portfolio-review";
  const showHeaderCta =
    !isApplicationPage &&
    !isProfilePage &&
    showFooter &&
    !isAuthenticated &&
    !isLoading &&
    // On mobile landing pages, defer to the hero's own CTA until scrolled past it.
    !(isMobile && isLandingPage && !pastHero);
  const headerCtaHref = isReviewPage
    ? PORTFOLIO_REVIEW_FREE_CAL_URL
    : INTRO_CALL_URL;
  const headerCtaLabel = isReviewPage
    ? "Book free 15 min"
    : "Book a free intro call";
  const mobileHeaderCtaLabel = isReviewPage ? "Book 15 min" : "Book a call";

  // Only keep header scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setScrolled(scrollTop > 10);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Track whether the hero (first section) has scrolled past, so the mobile
  // header CTA can appear from the second section onward on landing pages.
  useEffect(() => {
    if (!isLandingPage) {
      setPastHero(false);
      return;
    }
    const HEADER_OFFSET = 80; // roughly the header height
    const update = () => {
      const hero = document
        .getElementById("main-content")
        ?.querySelector("section");
      if (!hero) {
        setPastHero(window.scrollY > window.innerHeight * 0.7);
        return;
      }
      setPastHero(hero.getBoundingClientRect().bottom <= HEADER_OFFSET);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [isLandingPage, pathname]);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  // Handle keyboard shortcut (Cmd+K / Ctrl+K) to open search
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Cmd+K / Ctrl+K to open search
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        event.stopPropagation();
        setSearchOpen(true);
      }
      // Escape to close search
      if (event.key === "Escape" && searchOpen) {
        event.preventDefault();
        setSearchOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [searchOpen]);

  return (
    <div
      className="min-h-screen w-full bg-transparent flex flex-col"
      style={HEADER_LAYOUT_STYLE}
    >
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-white focus:px-4 focus:py-3 focus:text-[#231617]"
      >
        Skip to content
      </a>

      {/* Announcement Banner - shows on every public page, hides on scroll */}
      {showAnnouncement && (
        <div className="fixed top-0 left-0 right-0 z-[60] h-[var(--announcement-height)] bg-[#2B1B1C] text-white transition-all duration-300">
          <div className="container mx-auto flex h-full items-center justify-center px-4">
            <button
              onClick={() => setReportModalOpen(true)}
              className="flex items-center gap-2 whitespace-nowrap text-xs text-white/80 hover:opacity-70 transition"
            >
              <span className="hidden sm:inline">
                New: The State of Design Talent in Europe
              </span>
              <span className="sm:hidden">New: Design Talent in Europe</span>
              <span className="text-white">
                Download<span className="hidden sm:inline"> the report</span>
              </span>
              <Download className="h-3.5 w-3.5 text-white" />
            </button>
          </div>
        </div>
      )}

      {!isCustomDomain && (
        <header
          className={`fixed ${showAnnouncement ? "top-[var(--announcement-height)]" : "top-0"} left-0 right-0 z-50 w-full transition-all duration-300 ${
            isApplicationPage
              ? scrolled
                ? "glass-header-enhanced"
                : "bg-white"
              : pathname === "/get-verified"
                ? isMobile
                  ? "glass-header-enhanced"
                  : scrolled
                    ? "glass-header-enhanced"
                    : "bg-transparent"
                : isMobile || isProfilePage
                  ? "glass-header-enhanced"
                  : scrolled
                    ? "glass-header-enhanced"
                    : "bg-transparent"
          }`}
        >
          {/* The header shares the pages' 1104px content column. */}
          <div className="mx-auto flex h-[var(--header-height)] w-full max-w-[1168px] items-center justify-between gap-6 px-5 md:px-8">
            <div className="flex items-center">
              <LogoWithContextMenu />
            </div>

            {/* Center nav is xl-only. Leave the slot out of the row below xl so
                the empty item does not add a second gap and squeeze the logo. */}
            {!isProfilePage && (
              <div className="hidden items-center justify-center space-x-4 xl:flex">
                <HeaderNavigation scrolled={scrolled} />
              </div>
            )}

            <div className="flex  justify-end items-center space-x-3 sm:space-x-4">
              {/* Show notification bell for authenticated users */}
              {isAuthenticated && user && !isLoading && <NotificationBell />}
              {!isMobile && <AuthButton />}
              {showHeaderCta && (
                <a
                  href={headerCtaHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={headerCtaLabel}
                  className="inline-flex h-10 items-center justify-center whitespace-nowrap rounded-full bg-[#2B1B1C] px-3 text-xs font-medium text-white transition hover:bg-[#3a2327] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F97316] max-sm:h-12 max-sm:px-3.5 max-sm:text-sm sm:px-5 sm:text-sm"
                >
                  <span className="md:hidden">{mobileHeaderCtaLabel}</span>
                  <span className="hidden md:inline">{headerCtaLabel}</span>
                </a>
              )}
              {isMobile && <SignupButton />}
              {/* Mobile burger menu button */}
              {!isProfilePage && (
                <button
                  className="p-2 text-gray-600 hover:text-gray-800 transition-colors xl:hidden"
                  onClick={() => setMobileMenuOpen(true)}
                  aria-label="Open mobile menu"
                >
                  <Menu size={24} />
                </button>
              )}
            </div>
          </div>
        </header>
      )}

      {/* Mobile Menu */}
      {!isProfilePage && (
        <Dialog open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <DialogContent
            className="h-full flex flex-col justify-end items-center max-w-none bg-white/20 backdrop-blur-md border-none shadow-none"
            aria-describedby={undefined}
          >
            <DialogTitle className="sr-only">Navigation</DialogTitle>
            {/* Custom centered close button at the top */}
            <div className="">
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-md hover:bg-gray-50 transition-colors"
                aria-label="Close mobile menu"
              >
                <X size={16} className="text-gray-500" />
              </button>
            </div>

            {/* Center card container with max width */}
            <div className="relative w-full max-w-[700px] bg-white rounded-xl overflow-hidden flex flex-col h-[88vh] md:h-[90vh]">
              {/* Modal content */}
              <div className="flex-1 overflow-y-auto px-4 md:px-6">
                <div className="space-y-3 py-2">
                  <div className="space-y-1 py-2">
                    {[
                      {
                        heading: "Hire designers",
                        links: [...hiringLinks, eventsLink],
                      },
                      { heading: "For designers", links: designerLinks },
                    ].map((group) => (
                      <div key={group.heading} className="pb-2">
                        <p className="px-4 pb-1 pt-3 text-xs font-semibold uppercase tracking-[0.08em] text-gray-400">
                          {group.heading}
                        </p>
                        {group.links.map((link) => {
                          const current = isCurrentPage(pathname, link.href);
                          const className = `flex items-center gap-1.5 rounded-lg px-4 py-2.5 text-base font-medium text-gray-900 transition-colors hover:bg-gray-50 ${
                            current ? "bg-gray-100" : ""
                          }`;
                          return link.external ? (
                            <a
                              key={link.href}
                              href={link.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={className}
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              {link.title}
                              <ArrowUpRight
                                className="h-4 w-4 text-gray-400"
                                aria-hidden="true"
                              />
                            </a>
                          ) : (
                            <Link
                              key={link.href}
                              href={link.href}
                              aria-current={current ? "page" : undefined}
                              className={className}
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              {link.title}
                            </Link>
                          );
                        })}
                      </div>
                    ))}
                    {showHeaderCta && (
                      <a
                        href={headerCtaHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setMobileMenuOpen(false)}
                        className="w-full flex items-center justify-center py-3 px-4 mt-2 text-base font-medium text-white bg-[#2B1B1C] hover:bg-[#3a2327] rounded-lg transition-colors"
                      >
                        {headerCtaLabel}
                      </a>
                    )}
                    {isAuthenticated && !isLoading && (
                      <>
                        <hr className="my-3 border-gray-200" />
                        <Link
                          href="https://app.world-class.design/dashboard"
                          className="flex items-center py-3 px-4 text-base font-medium text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Dashboard
                        </Link>
                        <Link
                          href="https://app.world-class.design/profile"
                          className="flex items-center py-3 px-4 text-base font-medium text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Public Profile
                        </Link>
                        <Link
                          href="https://app.world-class.design/dashboard/settings"
                          className="flex items-center py-3 px-4 text-base font-medium text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Settings
                        </Link>
                        <Link
                          href="https://app.world-class.design/dashboard/notifications"
                          className="flex items-center py-3 px-4 text-base font-medium text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Notifications
                        </Link>
                        <hr className="my-3 border-gray-200" />
                        <button
                          onClick={async () => {
                            if (!isLoggingOut) {
                              setMobileMenuOpen(false);
                              await handleLogout();
                            }
                          }}
                          disabled={isLoggingOut}
                          className="w-full text-left flex items-center py-3 px-4 text-base font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isLoggingOut ? "Logging out..." : "Sign Out"}
                        </button>
                      </>
                    )}

                    {!isAuthenticated && !isLoading && (
                      <Link
                        href="https://app.world-class.design/auth"
                        className="flex items-center py-3 px-4 text-base font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors border-t border-gray-100 mt-4 pt-4"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Login
                      </Link>
                    )}
                    {/* Show loading state in mobile menu */}
                    {isLoading && (
                      <div className="flex items-center py-3 px-4 text-base font-medium text-gray-600 border-t border-gray-100 mt-4 pt-4">
                        <div className="inline-block h-4 w-4 border-t-2 border-gray-400 animate-spin rounded-full mr-2"></div>
                        Loading...
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Search Modal - Desktop Only */}
      {!isMobile && searchOpen && (
        <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
          <DialogContent
            className="top-[20vh] w-[calc(100%_-_2rem)] max-w-2xl translate-y-0 rounded-2xl border-none bg-white p-4 pt-12 shadow-xl"
            aria-describedby={undefined}
          >
            <DialogTitle className="sr-only">Search designers</DialogTitle>
            <button
              type="button"
              aria-label="Close search"
              onClick={() => setSearchOpen(false)}
              className="absolute right-2 top-2 rounded-full p-2"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="mx-auto w-full max-w-2xl">
              <SearchBar autoFocus />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Report Download Modal */}
      {reportModalOpen && (
        <ReportDownloadModal
          open={reportModalOpen}
          onOpenChange={setReportModalOpen}
        />
      )}

      {/* Add space for the 72px fixed header and optional 36px announcement. */}
      {!isCustomDomain && (
        <div
          className={
            isLandingPage
              ? "h-[var(--header-height)] bg-[#F8F9FB]"
              : showFooter
                ? "h-[calc(var(--announcement-height)+var(--header-height))] bg-transparent"
                : "h-[var(--header-height)] bg-transparent"
          }
        />
      )}

      <main
        id="main-content"
        tabIndex={-1}
        className={`flex-grow relative ${
          pathname === "/" ||
          pathname === "/report" ||
          pathname === "/portfolio-review"
            ? ""
            : "mb-8"
        }`}
      >
        {children}
      </main>

      {showFooter && !isCustomDomain && <SiteFooter />}
    </div>
  );
};

export default HomePageLayout;
